import React from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  MdBarChart,
  MdDashboard,
  MdHelp,
  MdHome,
  MdPerson,
  MdSettings,
} from 'react-icons/md'
import { GiReceiveMoney, GiPiggyBank } from 'react-icons/gi'
import PopupSection from './PopupSection'
import TooltipPopup from './TooltipPopup'
import { selectUserData } from 'containers/App/selectors'
import selectCustomers from 'containers/Customers/selectors'
import { chooseProfile, logout } from 'containers/App/actions'
import styles from './styles.module.scss'
import CustomerCompanySelector from './CustomerCompanySelector'
import Notifications from 'containers/Notifications'
import { updateUser } from 'containers/User/actions'
import { BsBoxArrowRight } from 'react-icons/bs'
import { FaExternalLinkAlt, FaLink } from 'react-icons/fa'
import { selectClaims } from 'containers/App/selectors'
import { HasClaim } from 'utils/Authorization'
import { useIsSystemUser } from 'utils/hooks/useHasClaim'
import { goToUrl, isLocal } from './functions'
import { injectIntl } from 'react-intl'
import messages from './messages'
import { createPathPrefix } from 'utils/context'

const CollapsedSidebar = ({ setIsVisible, intl: { formatMessage } }) => {
  const { companyCode, customerCode } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const user = useSelector(selectUserData)
  const permissions = useSelector((state) =>
    selectClaims(state, { customerCode, companyCode })
  )
  const customers = useSelector(selectCustomers)
  const isSystemUser = useIsSystemUser()
  const currentCustomer =
    customers.size === 1
      ? customers.first()
      : customers?.find((c) => c.code === customerCode)
  const currentCompany = currentCustomer?.companies?.find(
    (c) => c.code === companyCode
  )

  const handleThemeChange = () => {
    const theme = user.get('theme') === 'dark' ? 'light' : 'dark'
    dispatch(
      updateUser(user.toJS(), {
        ...user.toJS(),
        theme,
      })
    )
  }

  const Link = ({ claim, ...props }) => {
    return !claim || HasClaim({ permissions, claim }) ? (
      <RouterLink {...props} />
    ) : null
  }

  return (
    <div
      className={styles.collapsedSidebar}
      id="collapsedSidebar"
      onClick={(e) => {
        if (e.target.id === 'collapsedSidebar') {
          setIsVisible(true)
        }
      }}
    >
      <div className={styles.logoWrapper}>
        <CustomerCompanySelector
          currentCustomer={currentCustomer}
          currentCompany={currentCompany}
          onlyIcon={true}
        />
      </div>
      <div className={styles.seperator} />
      <button
        className={styles.toggleButton}
        onClick={() => setIsVisible(true)}
      >
        <TooltipPopup text={formatMessage(messages.toggleSidebar)}>
          <BsBoxArrowRight size={20} style={{ marginRight: '-5px' }} />
        </TooltipPopup>
      </button>
      {currentCompany && (
        <>
          <TooltipPopup
            to={
              user.get('defaultPage') ||
              `${customerCode}/${companyCode}/dashboards`
            }
            text={formatMessage(messages.home)}
          >
            <MdHome size={22} />
          </TooltipPopup>
          <TooltipPopup
            to={`${customerCode}/${companyCode}/dashboards`}
            text={formatMessage(messages.dashboards)}
          >
            <MdDashboard size={22} />
          </TooltipPopup>
          <TooltipPopup
            to={`${customerCode}/${companyCode}/budgets`}
            text={formatMessage(messages.budgets)}
          >
            <GiPiggyBank size={22} />
          </TooltipPopup>
          <TooltipPopup
            to={`${customerCode}/${companyCode}/reports`}
            text={formatMessage(messages.reports)}
          >
            <MdBarChart size={22} />
          </TooltipPopup>
          <TooltipPopup
            to={`${customerCode}/${companyCode}/cashflow`}
            text={formatMessage(messages.cashFlow)}
            claim="CashFlowRead"
          >
            <GiReceiveMoney size={22} />
          </TooltipPopup>
          <div className={styles.seperator} />
        </>
      )}

      {currentCompany?.links?.length > 0 && (
        <PopupSection
          title={
            <>
              <FaLink size={20} />
            </>
          }
          show={true}
          direction="down"
        >
          {currentCompany.links.map((link, index) =>
            isLocal({ url: link.url }) ? (
              <button
                key={index}
                onClick={() => goToUrl({ url: link.url, navigate })}
              >
                <span>{link.name}</span>
              </button>
            ) : (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>
                  {link.name}{' '}
                  <FaExternalLinkAlt
                    size={11}
                    style={{ marginLeft: '5px', marginTop: '-3px' }}
                  />
                </span>
              </a>
            )
          )}
        </PopupSection>
      )}

      <PopupSection
        title={
          <>
            <TooltipPopup text={formatMessage(messages.settings)}>
              <MdSettings size={22} />
            </TooltipPopup>
          </>
        }
        show={!!companyCode}
        direction="down"
      >
        <Link to={`${customerCode}/${companyCode}/accounts`} claim="Accounts">
          <span>{formatMessage(messages.accounts)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/accountingperiods`}
          claim="AccountingPeriodsRead"
        >
          <span>{formatMessage(messages.accountingPeriods)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/accountschemes`}
          claim="AccountSchemesRead"
        >
          <span>{formatMessage(messages.accountSchemes)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/accountschemetemplates`}
          claim="AccountSchemeTemplates"
        >
          <span>{formatMessage(messages.accountSchemeTemplates)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/calculationconstants`}
          claim="CalculationConstants"
        >
          <span>{formatMessage(messages.calculationConstants)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/dimensions`}
          claim="DimensionRead"
        >
          <span>{formatMessage(messages.dimensions)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/reportinggroups`}
          claim="ReportingGroupRead"
        >
          <span>{formatMessage(messages.reportingGroups)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/reportschemes`}
          claim="ReportsSchemesRead"
        >
          <span>{formatMessage(messages.reportSchemes)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/rollingrules`}
          claim="RollingRuleTemplatesRead"
        >
          <span>{formatMessage(messages.rollingRuleTemplates)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/vouchers`}
          claim="VoucherRead"
        >
          <span>{formatMessage(messages.vouchers)}</span>
        </Link>
        <Link to={`${customerCode}/${companyCode}/roles`} claim="RoleRead">
          <span>{formatMessage(messages.roles)}</span>
        </Link>
        <Link to={`${customerCode}/${companyCode}/users`} claim="UserRead">
          <span>{formatMessage(messages.users)}</span>
        </Link>
        <Link
          to={`${customerCode}/companies/${companyCode}`}
          claim="CompanyEdit"
        >
          <span>{formatMessage(messages.settings)}</span>
        </Link>
        <Link
          to={`${customerCode}/${companyCode}/integrationlogs`}
          claim="CompanyEdit"
        >
          <span>{formatMessage(messages.integrationLogs)}</span>
        </Link>
      </PopupSection>
      {/* Customer Settings */}
      {!companyCode && customerCode && (
        <PopupSection
          title={<MdSettings size={22} />}
          show={!!customerCode}
          direction="down"
        >
          <Link to={`${customerCode}/accountschemetemplates`}>
            <span>{formatMessage(messages.accountSchemeTemplates)}</span>
          </Link>
          <Link to={`${customerCode}/calculationconstants`}>
            <span>{formatMessage(messages.calculationConstants)}</span>
          </Link>
          <Link to={`${customerCode}`}>
            <span>{formatMessage(messages.companies)}</span>
          </Link>
          <Link to={`${customerCode}/customerdashboards`}>
            <span>{formatMessage(messages.dashboards)}</span>
          </Link>
          <Link to={`${customerCode}/reportschemes`}>
            <span>{formatMessage(messages.reportSchemes)}</span>
          </Link>
          <Link to={`${customerCode}/reportschemetemplates`}>
            <span>{formatMessage(messages.reportSchemeTemplates)}</span>
          </Link>
          <Link to={`${customerCode}/customerreports`}>
            <span>{formatMessage(messages.reports)}</span>
          </Link>
          <Link to={`customers/${customerCode}`}>
            <span>{formatMessage(messages.settings)}</span>
          </Link>
          <Link to={`${customerCode}/users`}>
            <span>{formatMessage(messages.users)}</span>
          </Link>
        </PopupSection>
      )}
      {/* System Settings */}
      {!companyCode && !customerCode && isSystemUser && (
        <PopupSection
          title={<MdSettings size={22} />}
          show={true}
          direction="down"
        >
          <Link to="/accountschemetemplates">
            <span>{formatMessage(messages.accountSchemeTemplates)}</span>
          </Link>
          <Link to="/allusers">
            <span>{formatMessage(messages.allUsers)}</span>
          </Link>
          <Link to="/calculationconstants">
            <span>{formatMessage(messages.calculationConstants)}</span>
          </Link>
          <Link to="/customers">
            <span>{formatMessage(messages.customers)}</span>
          </Link>
          <Link to="/systemdashboards">
            <span>{formatMessage(messages.dashboards)}</span>
          </Link>
          <a
            href={`${process.env.REACT_APP_GT_API}hangfire/`}
            target="_blank"
            rel="noreferrer"
          >
            <span>{formatMessage(messages.gtScheduledJobs)}</span>
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_API}hangfire`}
            target="_blank"
            rel="noreferrer"
          >
            <span>{formatMessage(messages.hangfireJobs)}</span>
          </a>
          <Link to="/integrationsourcesystems">
            <span>{formatMessage(messages.integrationSourceSystems)}</span>
          </Link>
          <Link to="/reportschemes">
            <span>{formatMessage(messages.reportSchemes)}</span>
          </Link>
          <Link to="/reportschemetemplates">
            <span>{formatMessage(messages.reportSchemeTemplates)}</span>
          </Link>
          <Link to="/systemreportselections-beta">
            <span>{formatMessage(messages.reports)}</span>
          </Link>
          <Link to="/users">
            <span>{formatMessage(messages.users)}</span>
          </Link>
        </PopupSection>
      )}
      <div className={styles.flexGrow} />
      <TooltipPopup
        text={formatMessage(messages.help)}
        to="https://lemondoc.atlassian.net/wiki/spaces/Finazilla"
      >
        <a
          href="https://lemondoc.atlassian.net/wiki/spaces/Finazilla"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.withIcon}
        >
          <MdHelp size={22} />
        </a>
      </TooltipPopup>
      <TooltipPopup text={formatMessage(messages.notifications)}>
        <Notifications collapsed={true} />
      </TooltipPopup>
      <div className={styles.seperator} />
      <PopupSection
        title={
          <TooltipPopup text={formatMessage(messages.userSettings)}>
            <span style={{ height: '48px' }}>
              <MdPerson size={22} />
            </span>
          </TooltipPopup>
        }
      >
        <button className={'noClose'} onClick={handleThemeChange}>
          {user.get('theme') === 'dark'
            ? formatMessage(messages.useLightTheme)
            : formatMessage(messages.useDarkTheme)}
        </button>
        <Link
          to={
            createPathPrefix(customerCode, companyCode) +
            `users/${user.get('id')}?tab=settings`
          }
        >
          <span>{formatMessage(messages.settings)}</span>
        </Link>
        <button
          onClick={() => {
            dispatch(chooseProfile(user, navigate))
          }}
        >
          <span>{formatMessage(messages.switchProfile)}</span>
        </button>
        <button
          onClick={() => {
            dispatch(logout())
          }}
        >
          <span>{formatMessage(messages.logout)}</span>
        </button>
      </PopupSection>
      <button
        className={styles.toggleButton}
        onClick={() => setIsVisible(true)}
      >
        <TooltipPopup text={formatMessage(messages.toggleSidebar)}>
          <BsBoxArrowRight size={20} style={{ marginRight: '-5px' }} />
        </TooltipPopup>
      </button>
    </div>
  )
}

export default injectIntl(CollapsedSidebar)
